<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="modelValue"
        class="auth-modal-backdrop"
        :dir="lang === 'fa' ? 'rtl' : 'ltr'"
        @click.self="closeModal"
      >
      <section
        class="auth-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-modal-title"
      >
        <!-- نوار بالای مودال در موبایل -->
        <div class="mobile-drag-line d-md-none"></div>

        <!-- دکمه بستن -->
        <button
          type="button"
          class="close-btn"
          :aria-label="currentLang.closeModal"
          @click="closeModal"
        >
          <i class="bi bi-x-lg"></i>
        </button>

        <!-- هدر -->
        <header class="text-center mb-4">
          <div class="auth-icon mx-auto mb-3">
            <i
              :class="step === 'mobile' ? 'bi bi-phone-fill' : 'bi bi-shield-lock-fill'"
            ></i>
          </div>

          <h2 id="auth-modal-title" class="auth-title mb-2">
            {{ step === 'mobile' ? currentLang.enterLearningWorld : currentLang.confirmMobile }}
          </h2>

          <p class="auth-description mb-0">
            <template v-if="step === 'mobile'">
              {{ currentLang.enterMobileDescription }}
            </template>

            <template v-else>
              {{ currentLang.enterCodeDescription }}
              <strong dir="ltr">{{ formattedMobile }}</strong>
            </template>
          </p>
        </header>

        <!-- مرحله ورود شماره -->
        <form v-if="step === 'mobile'" @submit.prevent="sendOtp">
          <label for="mobile" class="form-label custom-label">
            {{ currentLang.mobileLabel }}
          </label>

          <div class="mobile-input-wrapper" :class="{ 'is-invalid': mobileError }">
            <span class="country-code" dir="ltr">+98</span>

            <input
              id="mobile"
              v-model="mobile"
              type="tel"
              inputmode="numeric"
              autocomplete="tel"
              maxlength="11"
              class="form-control mobile-input"
              :placeholder="currentLang.mobilePlaceholder"
              @input="handleMobileInput"
            />
          </div>

          <small v-if="mobileError" class="error-message">
            <i class="bi bi-exclamation-circle"></i>
            {{ mobileError }}
          </small>

          <button
            type="submit"
            class="btn auth-submit-btn w-100 mt-4"
            :disabled="isLoading || !isValidMobile"
          >
            <span v-if="isLoading" class="spinner-border spinner-border-sm ms-2"></span>
            {{ isLoading ? currentLang.sendingOtp : currentLang.sendOtpBtn }}
            <i v-if="!isLoading" class="bi bi-arrow-left me-2"></i>
          </button>
        </form>

        <!-- مرحله ورود کد -->
        <form v-else @submit.prevent="verifyOtp">
          <div class="otp-inputs" dir="ltr">
            <input
              v-for="(_, index) in otpDigits"
              :key="index"
              :ref="(el) => setOtpRef(el, index)"
              v-model="otpDigits[index]"
              class="otp-box"
              type="text"
              inputmode="numeric"
              maxlength="1"
              autocomplete="one-time-code"
              @input="handleOtpInput(index, $event)"
              @keydown.backspace="handleBackspace(index, $event)"
              @paste="handlePaste"
            />
          </div>

          <small v-if="otpError" class="error-message text-center d-block mt-3">
            <i class="bi bi-exclamation-circle"></i>
            {{ otpError }}
          </small>

          <button
            type="submit"
            class="btn auth-submit-btn w-100 mt-4"
            :disabled="isLoading || otpCode.length !== 4"
          >
            <span v-if="isLoading" class="spinner-border spinner-border-sm ms-2"></span>
            {{ isLoading ? currentLang.verifyingOtp : currentLang.verifyOtpBtn }}
            <i v-if="!isLoading" class="bi bi-check2-circle me-2"></i>
          </button>

          <div class="otp-actions">
            <button type="button" class="text-btn" @click="backToMobile">
              <i class="bi bi-pencil-square"></i>
              {{ currentLang.editMobile }}
            </button>

            <span class="divider"></span>

            <button
              type="button"
              class="text-btn"
              :disabled="resendTimer > 0"
              @click="resendOtp"
            >
              <template v-if="resendTimer > 0">
                {{ currentLang.resendTimer }} {{ resendTimer }} {{ lang === 'fa' ? 'ثانیه' : 'sec' }}
              </template>
              <template v-else>
                <i class="bi bi-arrow-repeat"></i>
                {{ currentLang.resendCode }}
              </template>
            </button>
          </div>
        </form>

        <!-- فوتر -->
        <footer class="auth-footer">
          <i class="bi bi-shield-check"></i>
          {{ currentLang.secureInfo }}
        </footer>
      </section>
     </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { lang } from "@/plugins/lang";

import api from "@/plugins/axios";

// فرض بر این است که فایل‌های ترجمه در مسیر مشخص شده هستند
import enAuth from "@/locales/en/authModal";
import faAuth from "@/locales/fa/authModal";

const translations = {
  en: enAuth,
  fa: faAuth,
};
const currentLang = computed(() => translations[lang.value]);



const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue', 'success'])

const step = ref('mobile')
const mobile = ref('')
const mobileError = ref('')
const otpError = ref('')
const otpDigits = ref(['', '', '', ''])
const otpRefs = ref([])
const isLoading = ref(false)
const resendTimer = ref(0)
let timerInterval = null

const normalizedMobile = computed(() =>
  mobile.value
    .replace(/\s/g, '')
    .replace(/^(\+98|0098)/, '0')
)

const isValidMobile = computed(() => /^09\d{9}$/.test(normalizedMobile.value))

const formattedMobile = computed(() => {
  const value = normalizedMobile.value

  if (!value) return ''

  return value.replace(/^(\d{4})(\d{3})(\d{4})$/, '$1 $2 $3')
})

const otpCode = computed(() => otpDigits.value.join(''))

function handleMobileInput(event) {
  mobile.value = event.target.value.replace(/[^\d]/g, '').slice(0, 11)
  mobileError.value = ''
}

function setOtpRef(el, index) {
  if (el) otpRefs.value[index] = el
}

function handleOtpInput(index, event) {
  const digit = event.target.value.replace(/\D/g, '').slice(-1)

  otpDigits.value[index] = digit
  otpError.value = ''

  if (digit && index < otpDigits.value.length - 1) {
    otpRefs.value[index + 1]?.focus()
  }
}

function handleBackspace(index, event) {
  if (!otpDigits.value[index] && index > 0) {
    event.preventDefault()
    otpDigits.value[index - 1] = ''
    otpRefs.value[index - 1]?.focus()
  }
}

function handlePaste(event) {
  event.preventDefault()

  const pastedCode = event.clipboardData
    .getData('text')
    .replace(/\D/g, '')
    .slice(0, 4)

  if (!pastedCode) return

  otpDigits.value = pastedCode.padEnd(4, '').split('')

  nextTick(() => {
    const focusIndex = Math.min(pastedCode.length, 4)
    otpRefs.value[focusIndex]?.focus()
  })
}

async function sendOtp() {
  mobileError.value = ''

  if (!isValidMobile.value) {
    mobileError.value = 'لطفاً یک شماره موبایل معتبر وارد کنید.'
    return
  }

  isLoading.value = true

  try {
  
     const response = await api.post("/general/client/send-otp-code", {
      mobile: normalizedMobile.value,
    });

    if (response.status === 200) {
    await new Promise((resolve) => setTimeout(resolve, 900))
    step.value = 'otp'
    startResendTimer()

    nextTick(() => otpRefs.value[0]?.focus())
    }
  } catch (error) {
    mobileError.value = error.response?.data?.message || "networkError";
  } finally {
    isLoading.value = false
  }
}

async function verifyOtp() {
  otpError.value = ''

  if (otpCode.value.length !== 4) {
    otpError.value = 'لطفاً کد 4 رقمی را کامل وارد کنید.'
    return
  }

  isLoading.value = true

  try {


     const res  = await api.post("/general/login", {
      mobile: normalizedMobile.value,
      code: otpCode.value,
    });

    if (res.data.success === true && res.data.status === 200) {
      const user = res.data.data.user;
      const token = res.data.data.access_token;

      localStorage.setItem("auth_token", token);
      localStorage.setItem("user_info", JSON.stringify(user));

    await new Promise((resolve) => setTimeout(resolve, 900))

   // اطلاع به Header برای رندر مجدد
  emit('success', { token, user })

  // بستن مودال

    closeModal()
  }
  } catch (error) {
    if (error.response) {
      otpError.value = error.response.data.message || "خطای نامشخص";
    } else {
      otpError.value = error;
    }
  } finally {
    isLoading.value = false
  }
}

function backToMobile() {
  step.value = 'mobile'
  otpDigits.value = ['', '', '', '', '']
  otpError.value = ''
  clearTimer()
}

function resendOtp() {
  if (resendTimer.value > 0) return

  otpDigits.value = ['', '', '', '', '']
  otpError.value = ''
  startResendTimer()

  // درخواست ارسال مجدد OTP به بک‌اند
}

function startResendTimer() {
  clearTimer()
  resendTimer.value = 350

  timerInterval = setInterval(() => {
    resendTimer.value--

    if (resendTimer.value <= 0) {
      clearTimer()
    }
  }, 1000)
}

function clearTimer() {
  if (timerInterval) {
    clearInterval(timerInterval)
    timerInterval = null
  }
}

function closeModal() {
  clearTimer()
  emit('update:modelValue', false)
}

watch(
  () => props.modelValue,
  (isOpen) => {
    if (!isOpen) {
      step.value = 'mobile'
      otpDigits.value = ['', '', '', '', '']
      mobileError.value = ''
      otpError.value = ''
      clearTimer()
    }
  }
)
</script>

<style scoped>


.auth-modal-backdrop {
  position: fixed !important;
  z-index: 99999 !important; /* بالاتر از همه لایه‌ها */
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  height: 100dvh !important; /* برای رفع مشکل نوار آدرس مرورگرهای موبایل */
  margin: 0 !important;
  padding: 1rem;
  
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  
  background: rgba(22, 37, 59, 0.55);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  
  box-sizing: border-box;
}

.auth-modal {
  position: relative;
  width: 100%;
  max-width: 450px;
  
  /* جلوگیری از رانده شدن به لبه‌ها با مارجین خودکار */
  margin: auto !important;
  
  padding: 2.25rem;
  overflow: hidden;
  box-sizing: border-box;
  border: 1px solid rgba(255, 255, 255, 0.7);
  border-radius: 32px;
  background:
    radial-gradient(circle at 100% 0%, rgba(255, 203, 94, 0.28), transparent 33%),
    radial-gradient(circle at 0% 100%, rgba(123, 204, 255, 0.25), transparent 33%),
    rgba(255, 255, 255, 0.92);
  box-shadow: 0 24px 60px rgba(19, 42, 79, 0.28);
}




@media (max-width: 575.98px) {
  .auth-modal-backdrop {
    align-items: center !important;
    justify-content: center !important;
    padding: 1rem;
  }

  .auth-modal {
    width: 100%;
    max-width: calc(100vw - 2rem);
    margin: auto !important;
    min-height: auto;
    padding: 1.8rem 1.25rem 2rem;
    border-radius: 28px;
  }

  .modal-enter-from .auth-modal,
  .modal-leave-to .auth-modal {
    transform: scale(0.92);
  }
}

/* به جای left: 1rem از ویژگی استاندارد منطقی استفاده کنید */
.close-btn {
  position: absolute;
  top: 1rem;
  inset-inline-start: 1rem; /* در فارسی خودکار راست و در انگلیسی چپ قرار می‌گیرد */
  display: grid;
  width: 38px;
  height: 38px;
  place-items: center;
  border: 0;
  border-radius: 50%;
  color: #60708a;
  background: rgba(236, 242, 250, 0.9);
  transition: 0.2s ease;
}







.close-btn:hover {
  color: #fff;
  background: #ff6f7d;
  transform: rotate(90deg);
}

.auth-icon {
  display: grid;
  width: 72px;
  height: 72px;
  place-items: center;
  border: 6px solid rgba(255, 255, 255, 0.8);
  border-radius: 25px;
  color: #fff;
  font-size: 1.8rem;
  background: linear-gradient(135deg, #00ddd3, #00b8b0);
   box-shadow:
    0 7px 16px rgba(0, 205, 196, 0.3),
    0 3px 7px rgba(0, 120, 140, 0.14);
  transform: rotate(-6deg);
}

.auth-title {
color: #18324b;
  font-size: 1.35rem;
  font-weight: 800;
}

.auth-description {
  max-width: 330px;
  margin-inline: auto;
  color: #78869d;
  font-size: 0.9rem;
  line-height: 1.9;
}

.custom-label {
color: #18324b;
  font-size: 0.9rem;
  font-weight: 700;
}

.mobile-input-wrapper {
  display: flex;
  direction: ltr;
  overflow: hidden;
  border: 1px solid #e2e9f2;
  border-radius: 16px;
  background: #fff;
  transition: 0.2s ease;
}

.mobile-input-wrapper:focus-within {
  border-color: #7180f6;
  box-shadow: 0 0 0 4px rgba(0, 205, 196, 0.13);
}

.mobile-input-wrapper.is-invalid {
  border-color: #ee6573;
}

.country-code {
  display: flex;
  align-items: center;
  padding: 0 0.85rem;
  border-left: 1px solid #e8edf5;
  color: #60708a;
  font-size: 0.9rem;
  font-weight: 700;
  background: #f8faff;
}

.mobile-input {
  min-height: 53px;
  border: 0 !important;
  color: #18324b;
  font-size: 1rem;
  letter-spacing: 0.5px;
  box-shadow: none !important;
}

.mobile-input::placeholder {
  color: #bdc7d5;
  letter-spacing: 1px;
}

.error-message {
  display: inline-block;
  margin-top: 0.5rem;
  color: #e45161;
  font-size: 0.8rem;
}

.auth-submit-btn {
  min-height: 54px;
  border: 0;
  border-radius: 16px;
  color: #fff;
  font-weight: 700;
  background: linear-gradient(135deg, #00cdc4, #00b8b0);
  box-shadow: 0 10px 20px rgba(111, 112, 238, 0.25);
  transition: 0.25s ease;
}

.auth-submit-btn:hover:not(:disabled) {
  color: #fff;
  transform: translateY(-2px);
  box-shadow: 0 14px 25px rgba(111, 112, 238, 0.35);
}

.auth-submit-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.otp-inputs {
  display: flex;
  justify-content: center;
  gap: 0.55rem;
}

.otp-box {
  width: 48px;
  height: 56px;
  border: 1px solid #e1e8f1;
  border-radius: 14px;
  outline: none;
  color: #18324b;
  font-size: 1.3rem;
  font-weight: 800;
  text-align: center;
  background: #fff;
  transition: 0.2s ease;
}

.otp-box:focus {
  border-color: #7180f6;
  box-shadow: 0 0 0 4px rgba(113, 128, 246, 0.13);
  transform: translateY(-2px);
}

.otp-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  margin-top: 1.4rem;
  color: #74829a;
  font-size: 0.78rem;
}

.text-btn {
  border: 0;
  color: #6877d9;
  font-weight: 700;
  background: transparent;
}

.text-btn:disabled {
  color: #9aa6b8;
  cursor: default;
}

.divider {
  width: 1px;
  height: 18px;
  background: #dce3ed;
}

.auth-footer {
  margin-top: 1.75rem;
  padding-top: 1rem;
  border-top: 1px dashed #dce4ef;
  color: #99a6b8;
  font-size: 0.75rem;
  text-align: center;
}

.auth-footer i {
  margin-left: 0.3rem;
  color: #62b99b;
}
/* Transition */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.25s ease;
}

.modal-enter-active .auth-modal,
.modal-leave-active .auth-modal {
  transition: transform 0.28s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.28s ease;
  transform-origin: center center;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .auth-modal,
.modal-leave-to .auth-modal {
  opacity: 0;
  transform: scale(0.92); /* حذف translateY تا محور افقی/عمودی جا‌به‌جا نشود */
}

/* Mobile */
.mobile-drag-line {
  width: 42px;
  height: 5px;
  margin: -0.8rem auto 1.25rem;
  border-radius: 99px;
  background: #dce3ed;
}


</style>
